/**
 * Emoji picker for the Flux editor.
 *
 * On every flux:editor:ready, appends a 😀 button to that editor's toolbar.
 * Clicking the button opens a small grid of common emojis; clicking an emoji
 * inserts it via Flux's built-in editor.chain().focus().insertContent(...).
 *
 * Implemented purely in JS — no Blade markup changes — to avoid wrapping
 * <flux:editor> in extra DOM, which interferes with <ui-editor>'s mount-time
 * closest('ui-field') lookup when Livewire re-morphs the tree.
 */

const COMMON_EMOJIS = [
    '😀','😄','😊','😍','🥰','😎','🤩','🥳','😂','🤣',
    '😅','🫡','🤔','😮','😢','😡','👍','👏','🙌','💪',
    '❤️','🧡','💛','💚','💙','💜','🖤','🤍','⭐','🎉',
    '🎁','🔥','✨','💡','🚀','🌟','🎯','💯','✅','❌',
];

function buildEmojiPicker(editor) {
    document.getElementById('emoji-picker-panel')?.remove();

    const panel = document.createElement('div');
    panel.id = 'emoji-picker-panel';
    panel.className = [
        'absolute z-50',
        'bg-white dark:bg-zinc-800',
        'border border-zinc-200 dark:border-zinc-700',
        'rounded-lg shadow-lg p-2',
        'grid gap-0.5',
    ].join(' ');
    panel.style.gridTemplateColumns = 'repeat(8, 1fr)';

    COMMON_EMOJIS.forEach((emoji) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'text-xl p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700 cursor-pointer';
        btn.textContent = emoji;
        btn.addEventListener('mousedown', (e) => {
            e.preventDefault();
            editor.chain().focus().insertContent(emoji).run();
            panel.remove();
        });
        panel.appendChild(btn);
    });

    document.body.appendChild(panel);

    setTimeout(() => {
        const close = (e) => {
            if (!panel.contains(e.target)) {
                panel.remove();
                document.removeEventListener('mousedown', close);
            }
        };
        document.addEventListener('mousedown', close);
    }, 0);

    return panel;
}

function positionPickerNearButton(panel, btn) {
    const rect = btn.getBoundingClientRect();
    panel.style.top = `${rect.bottom + window.scrollY + 4}px`;
    panel.style.left = `${rect.left + window.scrollX}px`;
}

function buildToolbarButton() {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.dataset.editorEmoji = '';
    btn.title = 'Insert emoji';
    btn.setAttribute('aria-label', 'Insert emoji');
    btn.className = [
        'inline-flex items-center justify-center',
        'w-7 h-7 text-base rounded',
        'hover:bg-zinc-200/60 dark:hover:bg-white/10',
        'text-zinc-700 dark:text-zinc-300',
        'shrink-0',
    ].join(' ');
    btn.textContent = '😀';
    return btn;
}

document.addEventListener('flux:editor:ready', (event) => {
    const editor = event.detail?.editor;
    const uiEditor = event.target;
    if (!editor || !uiEditor) return;

    // Avoid double-inserting on Livewire re-morphs.
    const existing = uiEditor.querySelector('[data-editor-emoji]');
    if (existing) return;

    // Append the button inside <ui-toolbar>'s flex container.
    const toolbarInner = uiEditor.querySelector('ui-toolbar > div');
    if (!toolbarInner) return;

    const btn = buildToolbarButton();
    toolbarInner.appendChild(btn);

    btn.addEventListener('click', (e) => {
        e.preventDefault();
        const open = document.getElementById('emoji-picker-panel');
        if (open) { open.remove(); return; }
        const panel = buildEmojiPicker(editor);
        positionPickerNearButton(panel, btn);
    });
});
